import { Button } from '@chakra-ui/react'
import styled from '@emotion/styled'
import _ from 'lodash'
import { Component } from 'react'
import { GroupedItemRowProps, ProductInfoField } from 'stylewhere/api'
import {
  Box,
  Icons,
  ItemInfoModal,
  PrivateImage,
  RoundedLabel,
  SgtinInfoModal,
  StatusChip
} from 'stylewhere/components'
import { T, __ } from 'stylewhere/i18n'
import { AppStore } from 'stylewhere/shared'
import { closeModal, hasChecklist, openModal } from 'stylewhere/shared/utils'

interface State {
  modalVisible: boolean
}

type ItemDecodeResultType = 'itemFound' | 'productFound' | 'unknownDecoded' | 'unknown' | 'virgin'

export class GroupedItemRow extends Component<GroupedItemRowProps, State> {
  static Skeleton = styled(Box)``

  coreProductAttributes: ProductInfoField[] = [
    {
      label: __(T.misc.size),
      path: 'size.value'
    },
    { label: __(T.misc.color), path: 'color.value' }
  ]

  getShowItemModalHandler = (resultType: ItemDecodeResultType) => () => {
    const { decodedItem, hideIdentifiersByType, overrideProductInfoFields } = this.props
    if (decodedItem.item && !decodedItem.item.fakeItemID) {
      switch (resultType) {
        case 'itemFound':
          return openModal({
            id: 'item-modal',
            closeOnOverlayClick: true,
            modal: (
              <ItemInfoModal
                visible
                itemId={decodedItem.item.id}
                onClose={() => closeModal('item-modal')}
                hideIdentifiersByType={hideIdentifiersByType}
                overrideProductInfoFields={overrideProductInfoFields}
                readTid={decodedItem.item.readTid}
                retrieveTid={decodedItem.item.retrieveTid}
              />
            )
          })
        case 'productFound':
          return openModal({
            id: 'item-modal',
            closeOnOverlayClick: true,
            modal: <SgtinInfoModal visible item={decodedItem.item} onClose={() => closeModal('item-modal')} />
          })
      }
    }
  }

  isWarningZone = () => {
    const { operation, itemZoneBase, decodedItem } = this.props
    if (operation?.enabledCheckItemZone && itemZoneBase && decodedItem.item?.zone) {
      return decodedItem.item.zone.id !== itemZoneBase
    }
    return false
  }

  getProductCode = (resultType: ItemDecodeResultType) => {
    const { decodedItem } = this.props
    switch (resultType) {
      case 'itemFound':
      case 'productFound':
        return decodedItem.item?.product?.code
      case 'unknownDecoded':
        return decodedItem.item?.product?.attributes?.ean?.value
      case 'unknown':
      case 'virgin':
        return __(T.misc.unknown).toUpperCase()
    }
  }

  getIdentifierCode = (_resultType: ItemDecodeResultType) => {
    const { configuration, decodedItem, hideIdentifiersByType } = this.props
    if (configuration?.useProductCode && decodedItem?.item?.product) {
      return decodedItem.item.product?.code
    }
    if (hideIdentifiersByType?.includes('UHF_TAG')) return undefined
    return decodedItem.identifierCode
  }

  getIdentifiers = () => {
    const { configuration, decodedItem, hideIdentifiersByType } = this.props
    if (configuration?.hideIdentifiers) {
      return []
    } else {
      let identifiers = decodedItem.item?.identifiers ? decodedItem.item.identifiers : []
      identifiers = identifiers.filter((idf) => idf.status !== 'REPLACED_ASSOCIATION')
      if (hideIdentifiersByType) {
        identifiers = identifiers.filter((idf) => !hideIdentifiersByType.includes(idf.type))
      }
      if (!configuration?.useProductCode) {
        identifiers = identifiers.filter((idf) => idf.code !== decodedItem.identifierCode)
      }
      return identifiers
    }
  }

  getStatusForResultType = (
    resultType: ItemDecodeResultType,
    identifierStatus?: string
  ): [string, 'warning' | 'error' | 'default' | 'success'] => {
    if (identifierStatus === 'KILLED') {
      return [__(T.misc.killed), 'error']
    }
    switch (resultType) {
      case 'itemFound':
        return ['', 'success']
      case 'productFound':
        return [__(T.misc.tag_not_associated), 'warning']
      case 'unknownDecoded':
        return [__(T.misc.unknown_product), 'warning']
      case 'unknown':
        return [__(T.misc.unknown_tag), 'error']
      case 'virgin':
        return [__(T.misc.virgin_tag), 'error']
      default:
        return [__(T.misc.invalid_tag), 'error']
    }
  }

  sortStatusChip = (status: string) => {
    const { operation } = this.props
    const chipOrder = {
      error: -1,
      warning: 0,
      default: 1
    }
    let statusType = chipOrder.default
    if (operation?.itemStatuses.warning.includes(status)) statusType = chipOrder.warning
    if (operation?.itemStatuses.error.includes(status)) statusType = chipOrder.error

    return statusType
  }

  renderItemRow = (resultType: ItemDecodeResultType) => {
    const {
      decodedItem,
      onActionItem,
      hideProductInfo,
      hideIdentifiersZones,
      overrideProductInfoFields,
      mb,
      configuration,
      operation,
      disabledCounter,
      onRemoveItem
    } = this.props
    const { product } = decodedItem.item || {}
    const showCoreAttributes = resultType !== 'unknown' && resultType !== 'unknownDecoded' && resultType !== 'virgin'
    const identifiers = this.getIdentifiers()
    const identifierCode = this.getIdentifierCode(resultType)
    const productCode = this.getProductCode(resultType)
    const showIdentifier = identifierCode || identifiers.length > 0
    const [statusForType, statusIndicator] = this.getStatusForResultType(
      resultType,
      decodedItem.identifierStatus ?? undefined
    )
    const showItemDecodedStatuses = decodedItem.statuses && decodedItem.statuses.length > 0
    const read = decodedItem.detected ?? 0 // + (unexpected ?? 0)
    const withChecklist = hasChecklist(operation)
    const counterValue = decodedItem.expected
      ? `${read}/${decodedItem.expected}`
      : `${read}${withChecklist ? '/0' : ''}`
    const hasItemDetails = (['itemFound', 'productFound'] as ItemDecodeResultType[]).includes(resultType)

    const isNFC = decodedItem.tagType === 'NFC_TAG'
    return (
      <Row
        data-testid={`grouped-item-row`}
        row
        mb={mb}
        onClick={hasItemDetails ? this.getShowItemModalHandler(resultType) : undefined}
        p={15}
      >
        {!hideProductInfo && product?.code && AppStore.getShowProductImage() ? (
          <Box center>
            <PrivateImage data-testid={`product-image`} product={product} width={100} height={100} />
          </Box>
        ) : (
          <Box center>{isNFC ? <Icons.Nfc style={{ width: 100 }} /> : <Icons.Rfid style={{ width: 100 }} />}</Box>
        )}
        <Box flex justify={'center'} mr={0} ml={15}>
          {showIdentifier && (
            <Box row style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
              {identifierCode && (
                <RoundedLabel value={identifierCode} style={{ backgroundColor: '#333', color: '#FFF' }} />
              )}
              {identifiers.map((identifier, idx) => (
                <RoundedLabel
                  key={'identifier-label-' + idx}
                  style={{ backgroundColor: '#333', color: '#FFF' }}
                  label={identifier.role?.toUpperCase()}
                  value={identifier.code}
                />
              ))}
            </Box>
          )}
          {!hideProductInfo && (
            <Box data-testid="product-info" pv={0} ph={0} mt={showIdentifier ? 8 : 0} mb={2}>
              {!configuration?.useProductCode && (
                <b data-testid="product-code" style={{ fontSize: 23 }}>
                  {productCode}
                </b>
              )}
              <span data-testid="product-description" style={{ fontSize: 16 }}>
                {product?.description}
              </span>
              {showCoreAttributes && (
                <Box row style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                  {(overrideProductInfoFields ?? this.coreProductAttributes).map((field, idx) => (
                    <RoundedLabel
                      key={'product-info-' + idx}
                      label={field.label}
                      value={_.get(product, field.path ?? '', 'n/a')}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {!hideIdentifiersZones && decodedItem.item?.zone?.place && (
            <StatusChip
              status={`${decodedItem.item?.zone?.place.code} - ${decodedItem.item?.zone?.code}`}
              operation={operation}
              mt={8}
            />
          )}
          {(showItemDecodedStatuses || statusForType !== '') && (
            <Box row flexWrap>
              {showItemDecodedStatuses &&
                decodedItem.statuses
                  ?.toSorted(this.sortStatusChip)
                  .map((status, index) => (
                    <StatusChip key={'item-status-' + index} status={status} operation={operation} index={index} />
                  ))}
              {statusForType !== '' &&
                (!showItemDecodedStatuses ||
                  !decodedItem.statuses ||
                  decodedItem.statuses.filter((status) => status !== statusForType).length === 0) && (
                  <StatusChip status={statusForType.toUpperCase()} customStatusType={statusIndicator} />
                )}
            </Box>
          )}
          {this.isWarningZone() && (
            <StatusChip customStatusType="warning" status={__(T.misc.warning_zone_items)} operation={operation} />
          )}
        </Box>
        {withChecklist && !disabledCounter && decodedItem.expected > 0 && (
          <Box center ml={15}>
            <SmallCounter
              data-testid="counter"
              mr={0}
              ph={20}
              detected={read}
              expected={decodedItem.expected ?? 0}
              unexpected={decodedItem.unexpected ?? 0}
              noChecklist={!withChecklist}
            >
              {counterValue}
            </SmallCounter>
          </Box>
        )}
        {operation?.removeMode === 'identifier' && (
          <Button
            variant="solid"
            colorScheme="red"
            borderRadius={25}
            style={{ backgroundColor: 'lightgrey', height: 50, width: 50, borderRadius: 25 }}
            onClick={(e) => {
              e.stopPropagation()
              if (onRemoveItem) {
                onRemoveItem(decodedItem)
              }
            }}
          >
            <Icons.Trash />
          </Button>
        )}
        {!!onActionItem && (
          <Box center mr={10} onClick={() => onActionItem(this.props.decodedItem)}>
            <Icons.Confirmed style={{ width: 50 }} />
          </Box>
        )}
      </Row>
    )
  }

  render() {
    const { decodedItem } = this.props
    // 1. epc con item a sistema - itemFound
    // 2. epc decodificabile, item non a sistema, prodotto a sistema - productFound
    // 3. epc decodificabile, item non a sistema, prodotto non a sistema - unknownDecoded
    // 4. epc non decodificabile - renderUnknownItemRow

    let decodeStatus: ItemDecodeResultType
    if (decodedItem.item?.id) {
      decodeStatus = 'itemFound'
    } else if (decodedItem.item?.product?.id) {
      decodeStatus = 'productFound'
    } else if (decodedItem.item?.product?.attributes?.ean) {
      decodeStatus = 'unknownDecoded'
    } else if (
      decodedItem.item?.statuses &&
      Array.isArray(decodedItem.item.statuses) &&
      decodedItem.item.statuses.includes('VIRGIN_TAG')
    ) {
      decodeStatus = 'virgin'
    } else {
      decodeStatus = 'unknown'
    }

    return this.renderItemRow(decodeStatus)
  }
}

const Row = styled(Box)<{ mb? }>`
  margin-bottom: ${({ mb }) => (mb ? mb + 'px' : '20px')};
  border: 2px solid #edecec;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background-color: ${({ theme }) => theme.background2};
`

const SmallCounter = styled(Box)<{
  detected?: number
  expected?: number
  unexpected?: number
  noChecklist?: boolean
}>`
  font-size: 50px;
  font-weight: bold;
  text-align: center;
  min-width: 100px;
  width: 100%;
  align-self: center;
  background-color: ${({ detected = 0, expected = 0, unexpected = 0, noChecklist = false }) => {
    if (noChecklist) return '#F0F0F0'
    if (detected + unexpected > expected) return '#F2B57D'
    if (noChecklist) return '#75EBA8'
    if (unexpected > 0) return '#F2B57D'
    if (detected < expected) return '#EDEDED'
    if (expected > 0 && detected === expected) return '#75EBA8'
    return '#F0F0F0'
  }};
  border-radius: 10px;
`
